import { useState, useRef, useEffect } from 'react'
import { RefreshCw, Radio, Copy, Check, ChevronDown, ChevronUp, Trash2 } from 'lucide-react'
import { WEBHOOK_BASE } from './data'
import type { Creds, LogEntry, WebhookEvent } from './types'

function outcomeColor(event: WebhookEvent): { bg: string; border: string; text: string } {
  const t = String(event.eventType ?? '').toLowerCase()
  const result = String(event.payload?.result ?? event.payload?.workflowResult?.result ?? '').toUpperCase()
  if (result === 'PASS' || t.includes('pass') || t.includes('success'))
    return { bg: 'rgba(52,211,153,.08)', border: 'rgba(52,211,153,.3)', text: '#34d399' }
  if (result === 'FAIL' || t.includes('fail') || t.includes('block'))
    return { bg: 'rgba(248,113,113,.08)', border: 'rgba(248,113,113,.3)', text: '#f87171' }
  if (result === 'REVIEW' || t.includes('review'))
    return { bg: 'rgba(251,146,60,.08)', border: 'rgba(251,146,60,.3)', text: '#fb923c' }
  return { bg: 'rgba(139,92,246,.08)', border: 'rgba(139,92,246,.3)', text: '#a78bfa' }
}

export function WebhooksWorkspace({ creds }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const customerId = creds.customerId?.trim() || ''
  const webhookUrl = customerId ? `${WEBHOOK_BASE}/${customerId}` : null

  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  async function fetchEvents() {
    if (!customerId) return
    try {
      const res = await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId)}`)
      if (res.ok) {
        const data = await res.json() as WebhookEvent[]
        setEvents(data)
      }
    } catch { /* ignore */ }
  }

  async function clearEvents() {
    if (!customerId) return
    setClearing(true)
    try {
      await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' })
      setEvents([])
      setExpandedId(null)
    } catch { /* ignore */ } finally { setClearing(false) }
  }

  async function manualRefresh() {
    setLoading(true)
    await fetchEvents()
    setLoading(false)
  }

  useEffect(() => {
    void fetchEvents()
    pollingRef.current = setInterval(() => { void fetchEvents() }, 5000)
    return () => { if (pollingRef.current) clearInterval(pollingRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  function copyUrl() {
    if (!webhookUrl) return
    void navigator.clipboard.writeText(webhookUrl)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(390px, 430px) 1fr', gap: 16, alignItems: 'start' }}>

      {/* ── Left pane — endpoint info ──────────────────────────────────────── */}
      <div className="space-y-3">

        {/* Webhook URL card */}
        <div className="rounded-[10px] p-4 space-y-3" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
          <div className="flex items-center gap-2">
            <Radio size={13} style={{ color: '#fb923c' }} />
            <p className="text-[11px] font-semibold" style={{ color: '#e2e8f0' }}>Webhook Receiver</p>
            {webhookUrl && (
              <span className="ml-auto flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(52,211,153,.15)', color: '#34d399', border: '1px solid rgba(52,211,153,.3)' }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#34d399' }} />
                Listening
              </span>
            )}
          </div>

          <p className="text-[10px] leading-relaxed" style={{ color: '#94a3b8' }}>
            Each customer gets their own webhook URL based on their Customer ID — events from different customers are kept separate.
            Enter your credentials in the credential bar to generate your URL.
          </p>

          {!webhookUrl ? (
            <div className="rounded-[7px] px-3 py-2" style={{ background: 'rgba(251,146,60,.08)', border: '1px solid rgba(251,146,60,.25)' }}>
              <p className="text-[10px]" style={{ color: '#fb923c' }}>
                Enter your Customer ID in the credentials bar to generate your webhook URL.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-[7px] px-3 py-2" style={{ background: '#0a1322', border: '1px solid #1e2c44' }}>
                <code className="text-[10px] font-mono flex-1 break-all" style={{ color: '#fb923c' }}>{webhookUrl}</code>
                <button onClick={copyUrl} className="shrink-0 flex items-center gap-1 text-[10px] transition-colors" style={{ color: copiedUrl ? '#34d399' : '#5b6b85' }}>
                  {copiedUrl ? <Check size={11} /> : <Copy size={11} />}
                  {copiedUrl ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="rounded-[7px] p-3 space-y-1" style={{ background: 'rgba(251,146,60,.08)', border: '1px solid rgba(251,146,60,.25)' }}>
                <p className="text-[10px] font-semibold" style={{ color: '#fb923c' }}>How to configure</p>
                <ol className="list-decimal list-inside space-y-0.5 text-[10px] pl-1" style={{ color: '#94a3b8' }}>
                  <li>Copy the URL above</li>
                  <li>Go to FrankieOne portal → Settings → Webhooks (or email help@frankieone.com)</li>
                  <li>Add this URL as a webhook endpoint for your UAT environment</li>
                  <li>Trigger a workflow and watch events appear on the right</li>
                </ol>
              </div>
            </>
          )}
        </div>

        {/* JWT info card */}
        <div className="rounded-[10px] p-4 space-y-2" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
          <p className="text-[11px] font-semibold" style={{ color: '#e2e8f0' }}>JWT Verification (optional)</p>
          <p className="text-[10px] leading-relaxed" style={{ color: '#94a3b8' }}>
            FrankieOne can sign webhook payloads with a JWT in the{' '}
            <code className="font-mono text-[9px]" style={{ color: '#fb923c' }}>Authorization: Bearer</code>{' '}
            header using RSA-4096. To enable, contact{' '}
            <span className="font-mono" style={{ color: '#fb923c' }}>support@frankieone.com</span>{' '}
            — they will provide a customer-specific public key.
          </p>
          <p className="text-[10px] leading-relaxed" style={{ color: '#94a3b8' }}>
            When a JWT is present, the decoded payload is shown in each event. The{' '}
            <code className="font-mono text-[9px]" style={{ color: '#fb923c' }}>iss</code>{' '}
            claim should be{' '}
            <code className="font-mono text-[9px]" style={{ color: '#fb923c' }}>io.frankiefinancial.kycaml</code>.{' '}
            Signature verification requires the public key from FrankieOne — the display here is decode-only.
          </p>
        </div>
      </div>

      {/* ── Right pane — events list ───────────────────────────────────────── */}
      <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #1e2c44' }}>

        {/* Events header */}
        <div className="flex items-center justify-between px-3 py-2" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <div className="flex items-center gap-2">
            <p className="text-[11px] font-semibold" style={{ color: '#e2e8f0' }}>Incoming Events</p>
            {events.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-mono" style={{ background: 'rgba(251,146,60,.2)', color: '#fb923c' }}>
                {events.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void manualRefresh()}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] transition-colors"
              style={{ color: '#5b6b85' }}
            >
              <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            {events.length > 0 && (
              <button
                onClick={() => void clearEvents()}
                disabled={clearing}
                className="flex items-center gap-1 text-[10px] transition-colors"
                style={{ color: '#5b6b85' }}
              >
                <Trash2 size={10} /> Clear all
              </button>
            )}
          </div>
        </div>

        {/* Events body */}
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3" style={{ background: '#111c30' }}>
            <Radio size={28} style={{ color: '#1e2c44' }} />
            <p className="text-[11px] italic" style={{ color: '#5b6b85' }}>
              {!customerId
                ? 'Enter credentials to see your webhook events'
                : 'No events yet — configure the webhook endpoint above and trigger a workflow'}
            </p>
            {customerId && (
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#fb923c' }} />
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#fb923c' }} />
                </span>
                <span className="text-[10px]" style={{ color: '#fb923c' }}>Listening…</span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: '#111c30' }}>
            {events.map(ev => {
              const isExpanded = expandedId === ev.id
              const colors = outcomeColor(ev)
              const result = String(ev.payload?.result ?? ev.payload?.workflowResult?.result ?? ev.eventType ?? 'EVENT').toUpperCase()
              return (
                <div key={ev.id} className="p-3 space-y-2" style={{ borderBottom: '1px solid #1e2c44' }}>
                  <button
                    className="w-full flex items-center gap-2 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                  >
                    <span
                      className="px-2 py-0.5 rounded text-[9px] font-mono font-semibold shrink-0"
                      style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}
                    >
                      {result}
                    </span>
                    {ev.eventType && (
                      <span className="text-[9px] font-mono truncate" style={{ color: '#5b6b85' }}>{ev.eventType}</span>
                    )}
                    {ev.entityId && (
                      <span className="text-[9px] font-mono" style={{ color: '#94a3b8' }}>{ev.entityId}</span>
                    )}
                    <span className="ml-auto text-[9px] shrink-0" style={{ color: '#5b6b85' }}>
                      {new Date(ev.receivedAt).toLocaleString()}
                    </span>
                    {ev.jwtDecoded && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(251,146,60,.15)', color: '#fb923c', border: '1px solid rgba(251,146,60,.3)' }}>
                        JWT
                      </span>
                    )}
                    {isExpanded
                      ? <ChevronUp size={11} style={{ color: '#5b6b85', flexShrink: 0 }} />
                      : <ChevronDown size={11} style={{ color: '#5b6b85', flexShrink: 0 }} />}
                  </button>

                  {isExpanded && (
                    <div className="space-y-2">
                      {ev.jwtDecoded && (
                        <div className="rounded-[7px] p-2 space-y-1" style={{ background: 'rgba(251,146,60,.08)', border: '1px solid rgba(251,146,60,.25)' }}>
                          <p className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: '#fb923c' }}>JWT Decoded Payload</p>
                          <pre className="text-[9px] font-mono overflow-auto max-h-32" style={{ color: '#94a3b8' }}>
                            {JSON.stringify(ev.jwtDecoded, null, 2)}
                          </pre>
                        </div>
                      )}
                      {Object.keys(ev.headers).length > 0 && (
                        <div className="rounded-[7px] p-2 space-y-1" style={{ background: '#0e1828', border: '1px solid #1e2c44' }}>
                          <p className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: '#5b6b85' }}>Headers</p>
                          {Object.entries(ev.headers).map(([k, v]) => (
                            <div key={k} className="flex gap-2 text-[9px] font-mono">
                              <span className="shrink-0" style={{ color: '#5b6b85' }}>{k}:</span>
                              <span className="break-all" style={{ color: '#94a3b8' }}>
                                {k.toLowerCase() === 'authorization' ? v.slice(0, 30) + '…' : v}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="rounded-[7px] p-2 space-y-1" style={{ background: '#0a1322', border: '1px solid #1e2c44' }}>
                        <p className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: '#5b6b85' }}>Payload</p>
                        <pre className="text-[9px] font-mono overflow-auto max-h-96" style={{ color: '#94a3b8' }}>
                          {JSON.stringify(ev.payload, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {/* Listening indicator */}
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#fb923c' }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#fb923c' }} />
              </span>
              <span className="text-[9px]" style={{ color: 'rgba(251,146,60,.7)' }}>Listening for webhooks…</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
